import type { ReactElement } from 'react';
import { currencyLabel, formatMinorUnits } from '../../lib/format/money.js';
import { formatInterval, humaniseCode } from '../../lib/format/interval.js';
import type { PeriodRate } from '../../lib/api/queries/equipment.js';

export interface RateCardTableProps {
  rates: PeriodRate[];
  currency: string;
}

/**
 * The period-rate table from P-Q014.
 *
 * It displays and does not compute. There is no per-day division of a weekly
 * rate and no cheapest-rate marker: both would be a pricing rule the API never
 * stated. Amounts arrive as strings of minor units and are rendered in tabular
 * monospace, right-aligned, so a column aligns on the decimal (§3).
 *
 * Below `md` the table scrolls inside its own container — the page itself never
 * scrolls sideways.
 */
export function RateCardTable({ rates, currency }: RateCardTableProps): ReactElement {
  const label = currencyLabel(currency);
  return (
    <div className="table-scroll">
      <table className="rate-table">
        <caption className="rate-table__caption">
          Rates in {label}, before any taxes or fees.
        </caption>
        <thead>
          <tr>
            <th scope="col">Period</th>
            <th scope="col">Length</th>
            <th scope="col" className="rate-table__amount">
              Rate
            </th>
            <th scope="col" className="rate-table__amount">
              Included usage
            </th>
          </tr>
        </thead>
        <tbody>
          {rates.map((rate) => (
            <tr key={`${rate.period_type}-${rate.period_length}`}>
              <th scope="row">{humaniseCode(rate.period_type)}</th>
              <td>{formatInterval(rate.period_length)}</td>
              <td className="rate-table__amount numeric">
                <span className="rate-table__currency">{label}</span>
                {formatMinorUnits(rate.rate_amount_minor, currency)}
              </td>
              <td className="rate-table__amount numeric">
                {rate.included_usage_hours === null ? '—' : `${rate.included_usage_hours} h`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
